import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const handleError = (error) => {
  console.error('User Node API Error:', error);

  if (error.response?.status === 503) {
    return {
      success: false,
      error: 'Service temporarily unavailable'
    };
  }

  if (error.response?.status === 401) {
    return {
      success: false,
      error: 'Unauthorized - Invalid credentials'
    };
  }

  if (error.response?.status === 400) {
    return {
      success: false,
      error: error.response?.data?.message || 'Bad request - Invalid data provided'
    };
  }

  return {
    success: false,
    error: error.response?.data?.message || error.message || 'Operation failed'
  };
};

/**
 * Assigns a node to multiple users
 * @param {Object} params - Parameters for the request
 * @param {string} params.nodeId - Unique identifier of the node
 * @param {Array<string>} params.userIds - List of user IDs to assign the node to
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status
 */
export const assignNodeToUsers = async ({ nodeId, userIds, token }) => {
  try {
    const response = await axios.post(`${BASE_URL}/compute-v2/v2/user/node`, {
      node_id: nodeId,
      user_ids: userIds
    }, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json',
        'x-auth-user': token
      }
    });

    // Check if there are errors in the response data
    if (response.data?.errors && Object.keys(response.data.errors).length > 0) {
      const errorMessages = Object.values(response.data.errors);
      return {
        success: false,
        error: errorMessages.join('; '),
        data: response.data,
        status: response.status
      };
    }

    return {
      success: true,
      data: response.data,
      status: response.status,
      message: 'Node assigned to users successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Removes a node from a user
 * @param {Object} params - Parameters for the request
 * @param {string} params.nodeId - Unique identifier of the node
 * @param {string} params.userId - Unique identifier of the user
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status
 */
export const removeNodeFromUser = async ({ nodeId, userId, token }) => {
  try {
    const response = await axios.delete(`${BASE_URL}/compute-v2/v2/user/${userId}/node/${nodeId}`, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: response.data,
      status: response.status,
      message: 'Node removed from user successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};

/**
 * Gets all nodes of a user
 * @param {Object} params - Parameters for the request
 * @param {string} params.userId - Unique identifier of the user
 * @param {string} params.token - Authentication token
 * @returns {Promise<Object>} Response object with success/error status and node IDs
 */
export const getUserNodes = async ({ userId, token }) => {
  try {
    const response = await axios.get(`${BASE_URL}/compute-v2/v2/user/${userId}/node`, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'x-auth-user': token
      }
    });

    return {
      success: true,
      data: {
        nodeIds: response.data.node_ids || response.data.nodeIds || []
      },
      status: response.status,
      message: 'User nodes retrieved successfully'
    };
  } catch (error) {
    return handleError(error);
  }
};
